import { useEffect } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import { useSocket } from '@/contexts/SocketContext';
import { channelDashboardApi } from '../services/channelDashboardApi';

const activityKey = (channelId: string | undefined): unknown[] =>
  ['channel-dashboard', channelId, 'activity'];

/**
 * Timeline de atividade paginada por cursor (keyset). NÃO é page-based:
 * `initialPageParam` é undefined e o próximo cursor vem de `nextCursor` do backend.
 */
export const useChannelActivity = (channelId: string | undefined) =>
  useInfiniteQuery({
    queryKey: activityKey(channelId),
    queryFn: ({ pageParam }) =>
      channelDashboardApi.listActivity(channelId!, { limit: 25, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!channelId,
    staleTime: 30_000,
  });

/** Escuta channel:activity-received e invalida a timeline (string literal — convenção channels, bug pre-bundle Vite). */
export const useChannelActivitySocket = (channelId: string | undefined): void => {
  const { socket } = useSocket();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!socket || !channelId) return;
    const onReceived = (e: { channelId: string }): void => {
      if (e.channelId === channelId) {
        void queryClient.invalidateQueries({ queryKey: activityKey(channelId), exact: false });
      }
    };
    socket.on('channel:activity-received', onReceived);
    return () => {
      socket.off('channel:activity-received', onReceived);
    };
  }, [socket, channelId, queryClient]);
};
